import React, { useState, useEffect } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { toast } from 'react-toastify';

import {
  GET_PROMOTIONAL_REPORTS_BY_DATE,
  CHECK_FILE_EXISTENCE_BY_NAME,
  GET_ALL_UNIQUE_FILENAMES
} from '../../../graphql/queries';
import { CREATE_PROMOTIONAL_REPORT } from '../../../graphql/mutations';
import LoadingSpinner from '../../../components/layout/LoadingSpinner';
import {
  FaUpload,
  FaShareAlt,
  FaBroadcastTower,
  FaTv,
  FaNewspaper,
  FaFileAlt,
  FaQuestion,
  FaChevronRight,
  FaTimes,
  FaUsers,
  FaChartBar,
  FaFile
} from 'react-icons/fa';
import './Promo_Activities.css';

// Helper function to get media type icon
const getMediaTypeIcon = (mediaName) => {
  switch (mediaName) {
    case 'Social Media':
      return <FaShareAlt className="media-icon" />;
    case 'Radio':
      return <FaBroadcastTower className="media-icon" />;
    case 'Television':
      return <FaTv className="media-icon" />;
    case 'Newspaper':
      return <FaNewspaper className="media-icon" />;
    case 'Flyers/Posters':
      return <FaFileAlt className="media-icon" />;
    default:
      return <FaQuestion className="media-icon" />;
  }
};

// Format large numbers with commas
const formatNumber = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const PromoActivities = () => {
  const XLSX = require('xlsx');

  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [reports, setReports] = useState([]);
  const [file, setFile] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uniqueFileNames, setUniqueFileNames] = useState([]);
  const [totalCustomersTargeted, setTotalCustomersTargeted] = useState(0);
  const [totalPromotionalActivities, setTotalPromotionalActivities] = useState(0);
  const [mediaTypeData, setMediaTypeData] = useState([]);

  // Queries & Mutations
  const [checkFileExistenceByName] = useLazyQuery(CHECK_FILE_EXISTENCE_BY_NAME, {
    fetchPolicy: 'network-only'
  });

  const [fetchUniqueFileNames, { data: fileNameData, loading: fileNamesLoading }] = useLazyQuery(
    GET_ALL_UNIQUE_FILENAMES,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        if (data) {
          const allFileNames = data.listPromotionalReports.items.map(
            (item) => item.UploadedFileName
          );
          const uniqueNames = Array.from(new Set(allFileNames));

          // Sort by the trailing number in the filename (if any)
          const sortedByNumberEnd = uniqueNames.sort((a, b) => {
            const pattern = /\s(\d+)\./;
            const aMatch = a.match(pattern);
            const bMatch = b.match(pattern);
            const aNum = aMatch ? parseInt(aMatch[1], 10) : 0;
            const bNum = bMatch ? parseInt(bMatch[1], 10) : 0;
            return aNum - bNum;
          });

          setUniqueFileNames(sortedByNumberEnd);
        }
      }
    }
  );

  // Fetch unique file names on component mount
  useEffect(() => {
    fetchUniqueFileNames();
  }, [fetchUniqueFileNames]);

  // Lazy Query to fetch promotional reports
  const [fetchReports] = useLazyQuery(GET_PROMOTIONAL_REPORTS_BY_DATE, {
    fetchPolicy: 'network-only'
  });

  const [createReport] = useMutation(CREATE_PROMOTIONAL_REPORT, {
    onError: (error) => {
      console.error('Error uploading data:', error);
    }
  });

  // Helper function to load ALL pages of data
  const loadAllReportsPaginated = async (start, end) => {
    let allItems = [];
    let nextToken = null;

    do {
      const { data } = await fetchReports({
        variables: {
          startDate: new Date(start).toISOString().split('T')[0],
          endDate: new Date(end).toISOString().split('T')[0],
          limit: 1000,
          nextToken: nextToken
        }
      });

      if (data && data.listPromotionalReports) {
        allItems = [...allItems, ...data.listPromotionalReports.items];
        nextToken = data.listPromotionalReports.nextToken || null;
      } else {
        nextToken = null;
      }
    } while (nextToken);

    setReports(allItems);

    // Calculate Totals
    const totalCustomers = allItems.reduce(
      (acc, report) => acc + (report.numberOfConsumersTargeted || 0),
      0
    );
    setTotalCustomersTargeted(totalCustomers);
    setTotalPromotionalActivities(allItems.length);

    // Media Type categorization logic
    function categorizeMediaType(rawMediaType) {
      let mediaTypeStr = (rawMediaType || '').toLowerCase();

      // Minor auto-corrections for typos
      mediaTypeStr = mediaTypeStr.replace('facbook', 'facebook');

      const categories = [];

      if (
        mediaTypeStr.includes('facebook') ||
        mediaTypeStr.includes('instagram') ||
        mediaTypeStr.includes('twitter') ||
        mediaTypeStr.includes('x')
      ) {
        categories.push('Social Media');
      }
      if (mediaTypeStr.includes('radio')) {
        categories.push('Radio');
      }
      if (mediaTypeStr.includes('television') || mediaTypeStr.includes('tv')) {
        categories.push('Television');
      }
      if (mediaTypeStr.includes('newspaper')) {
        categories.push('Newspaper');
      }
      if (mediaTypeStr.includes('flyers') || mediaTypeStr.includes('posters')) {
        categories.push('Flyers/Posters');
      }
      if (mediaTypeStr.includes('social media')) {
        if (!categories.includes('Social Media')) {
          categories.push('Social Media');
        }
      }

      // If no categories were found, default to "Other"
      if (categories.length === 0) {
        categories.push('Other');
      }

      return categories;
    }

    // Aggregate results for each category
    const mediaTypeTotals = {
      'Social Media': 0,
      Radio: 0,
      Television: 0,
      Newspaper: 0,
      'Flyers/Posters': 0,
      Other: 0
    };

    allItems.forEach((report) => {
      const categoriesFound = categorizeMediaType(report.mediaType);
      const count = report.numberOfConsumersTargeted || 0;

      categoriesFound.forEach((cat) => {
        if (mediaTypeTotals[cat] === undefined) {
          mediaTypeTotals[cat] = 0;
        }
        mediaTypeTotals[cat] += count;
      });
    });

    // Convert the object to an array for displaying
    const mediaTypeArray = Object.keys(mediaTypeTotals).map((key) => ({
      name: key,
      count: mediaTypeTotals[key]
    }));
    setMediaTypeData(mediaTypeArray);
  };

  // Handle user selecting a date range
  const handleDateChange = (e, type) => {
    const newDateRange = { ...dateRange, [type]: e.target.value };
    setDateRange(newDateRange);

    if (newDateRange.start && newDateRange.end) {
      handleDateSelection(newDateRange.start, newDateRange.end);
    }
  };

  const handleDateSelection = async (start, end) => {
    if (start && end) {
      setIsLoading(true);
      try {
        await loadAllReportsPaginated(start, end);
      } catch (error) {
        console.error('Error loading all reports:', error);
      }
      setIsLoading(false);
    }
  };

  // File Upload Handling
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleFileUpload = async () => {
    if (!file) {
      alert('Please select an Excel file.');
      return;
    }

    setIsLoading(true);
    // Check if file with same name was uploaded
    const { data } = await checkFileExistenceByName({
      variables: { UploadedFileName: file.name }
    });
    const fileExists = data?.listPromotionalReports?.items?.length > 0;

    if (fileExists) {
      alert('This file has already been uploaded.');
      setIsLoading(false);
      return;
    }

    const reader = new FileReader();

    const processWorkbook = async (event, fileName) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: 'array' });

      // console.log('XLSX object:', XLSX); // <== Check what's actually defined
      // console.log('XLSX.utils:', XLSX.utils); // <== Should not be undefined
      // console.log('Workbook:', workbook);

      let isFileType1 = false;
      let isFileType2 = false;

      if (workbook.Sheets['Promotional Activities ']) {
        isFileType1 = true;
      } else if (workbook.Sheets['Marketing_Promotions']) {
        isFileType2 = true;
      } else {
        alert(
          "Unknown sheet name. Please ensure the sheet is named 'Promotional Activities ' or 'Marketing_Promotions'."
        );
        setIsLoading(false);
        return;
      }

      const promoSheet = isFileType1
        ? workbook.Sheets['Promotional Activities ']
        : workbook.Sheets['Marketing_Promotions'];
      const jsonData = XLSX.utils.sheet_to_json(promoSheet, { header: 1 });

      await processExcelData(jsonData, isFileType1, fileName);
    };

    reader.onload = (event) => {
      processWorkbook(event, file.name).catch((err) => {
        console.error('Error processing workbook:', err);
        setIsLoading(false);
      });
    };

    reader.readAsArrayBuffer(file);
  };

  const processExcelData = async (data, isFileType1, fileName) => {
    const rows = data.slice(1); // Skip header
    const validProcessedRows = [];
    const errors = [];

    for (const [index, row] of rows.entries()) {
      const rowNum = index + 2;

      // Skip rows that are completely empty
      if (!row || row.every(cell => cell === null || cell === undefined || String(cell).trim() === '')) {
        continue;
      }

      let activityName, activityDescription, activityDate, numberOfConsumersTargeted, mediaType;

      if (isFileType1) {
        [activityName, activityDate, numberOfConsumersTargeted, mediaType, activityDescription] = row;
      } else {
        [activityName, activityDescription, activityDate, numberOfConsumersTargeted, mediaType] = row;
      }

      // Validate presence
      if (
        !activityName || !activityDescription || !activityDate ||
        numberOfConsumersTargeted === undefined || numberOfConsumersTargeted === null || !mediaType
      ) {
        errors.push(`Row ${rowNum}: Missing required fields`);
        continue;
      }

      // Validate number
      numberOfConsumersTargeted = parseInt(numberOfConsumersTargeted, 10);
      if (isNaN(numberOfConsumersTargeted)) {
        errors.push(`Row ${rowNum}: "Number of Consumers Targeted" is not a valid number`);
        continue;
      }

      // Format strings
      activityName = String(activityName).trim();
      activityDescription = String(activityDescription).trim();
      mediaType = String(mediaType).trim();

      // Validate and format date
      let formattedDateTime;
      try {
        if (typeof activityDate === 'number') {
          const parsed = XLSX.SSF.parse_date_code(activityDate);
          const jsDate = new Date(parsed.y, parsed.m - 1, parsed.d, parsed.H || 9, parsed.M || 0);
          if (isNaN(jsDate.getTime())) throw new Error();
          formattedDateTime = jsDate.toISOString();
        } else {
          const jsDate = new Date(activityDate);
          if (isNaN(jsDate.getTime())) throw new Error();
          formattedDateTime = jsDate.toISOString();
        }
      } catch {
        errors.push(`Row ${rowNum}: Invalid date "${activityDate}"`);
        continue;
      }

      validProcessedRows.push({
        activityName,
        activityDescription,
        activityDateTime: formattedDateTime,
        numberOfConsumersTargeted,
        mediaType,
        UploadedFileName: fileName,
      });
    }

    // Show validation errors and cancel submission
    if (errors.length > 0) {
      alert("The following rows have errors:\n\n" + errors.join("\n"));
      setIsLoading(false);
      return;
    }

    // Proceed with upload
    let processedRows = 0;
    for (const input of validProcessedRows) {
      try {
        await createReport({ variables: { input } });
        processedRows++;
      } catch (err) {
        console.error(`Error uploading:`, err);
        alert(`Error uploading row with activity: ${input.activityName}`);
      }
    }

    alert(`Upload complete! ${processedRows} reports created.`);
    setIsLoading(false);
    setShowUploadModal(false);
    fetchUniqueFileNames();
  };

  const toggleUploadModal = () => {
    setShowUploadModal(!showUploadModal);
    setFile(null); // Reset file when toggling modal
  };

  return (
    <div className="promo-activities-container">
      {/* Header Section */}
      <div className="promo-header">
        <h1>Promotional Activities</h1>
      </div>

      {/* Loading Indicator */}
      {isLoading && <LoadingSpinner />}

      {/* Date Range and Upload Section */}
      <div className="date-filter-container">
        <div className="date-selection">
          <h2>Select Date Range</h2>
          <div className="date-inputs">
            <div className="date-field">
              <label>Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => handleDateChange(e, 'start')}
                className="date-input"
              />
            </div>
            <div className="date-field">
              <label>End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => handleDateChange(e, 'end')}
                className="date-input"
              />
            </div>
          </div>
        </div>

        <button className="upload-btn" onClick={toggleUploadModal}>
          <FaUpload className="btn-icon" />
          <span>Upload Data</span>
        </button>
      </div>

      {/* Analytics Cards */}
      <div className="analytics-cards">
        <div className="analytics-card">
          <div className="card-header">
            <h3>Total Customers Targeted</h3>
          </div>
          <div className="card-content">
            <div className="card-value-container">
              <FaUsers className="card-icon" />
              <span className="card-value">{formatNumber(totalCustomersTargeted)}</span>
            </div>
          </div>
        </div>

        <div className="analytics-card">
          <div className="card-header">
            <h3>Total Promotional Activities</h3>
          </div>
          <div className="card-content">
            <div className="card-value-container">
              <FaChartBar className="card-icon" />
              <span className="card-value">{totalPromotionalActivities}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Media Types Section */}
      <div className="media-type-section">
        <div className="media-card">
          <div className="card-header">
            <div className="header-with-icon">
              <FaChartBar className="header-icon" />
              <h3>Customers Targeted by Media Type</h3>
            </div>
          </div>
          <div className="media-list">
            {mediaTypeData.map((item, index) => (
              <div key={index} className="media-item">
                <div className="media-name">
                  {getMediaTypeIcon(item.name)}
                  <span>{item.name}</span>
                </div>
                <div className="media-count">
                  <span className="count-value">{formatNumber(item.count)}</span>
                  <FaChevronRight className="chevron-icon" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Uploaded Files Section */}
      <div className="files-section">
        <div className="files-card">
          <div className="card-header">
            <div className="header-with-icon">
              <FaFile className="header-icon" />
              <h3>Uploaded Files</h3>
            </div>
          </div>
          <div className="files-list">
            {fileNamesLoading ? (
              <div className="loading-files">Loading files...</div>
            ) : (
              uniqueFileNames.map((fileName, index) => (
                <div key={index} className="file-item">
                  <FaFile className="file-icon" />
                  <span className="file-name">{fileName}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="PA-modal-overlay">
          <div className="PA-upload-modal">
            <div className="PA-modal-header">
              <h2>Upload Promotional Data</h2>
              <button className="PA-close-modal" onClick={toggleUploadModal}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-separator"></div>

            <div className="modal-content">
              <div
                className="file-drop-area"
                onClick={() => document.getElementById('file-input').click()}
              >
                <input
                  id="file-input"
                  type="file"
                  className="hidden-input"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                />
                <FaUpload className="upload-icon" />
                <p className="file-name">
                  {file ? file.name : 'Click to select Excel file'}
                </p>
                <p className="file-hint">
                  Supports .xlsx and .xls formats
                </p>
              </div>

              <div className="modal-actions">
                <button
                  className="cancel-btn"
                  onClick={toggleUploadModal}
                >
                  Cancel
                </button>
                <button
                  className="submit-btn"
                  onClick={handleFileUpload}
                  disabled={!file || isLoading}
                >
                  {isLoading ? 'Processing...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoActivities;
